import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, post, type Connection } from "../../lib/api";
import { formatDate, KIND_LABELS } from "../../lib/format";
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Loading, Modal, PageHeader, Select, StatusBadge, Table, Td } from "../../components/ui";
import { can, useCatalog, useConnections, useWorkspace, useWorkspaceId } from "./hooks";

export function ConnectionsPage() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const detail = useWorkspace(ws);
  const conns = useConnections(ws);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const manage = can(detail.data?.permissions, "connections.manage");

  const refresh = () => qc.invalidateQueries({ queryKey: ["connections", ws] });

  return (
    <>
      <PageHeader title="Connections" subtitle="The systems you migrate from and to. Credentials are encrypted and never shown again." actions={manage && <Button onClick={() => setAdding(true)}>Add connection</Button>} />
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Card>
        {conns.isLoading ? (
          <Loading />
        ) : conns.data?.length ? (
          <Table head={["Name", "Type", "Account", "Status", "Last tested", ""]}>
            {conns.data.map((c) => (
              <tr key={c.id}>
                <Td>
                  <span className="font-medium">{c.name}</span> {c.readOnly && <Badge>read-only</Badge>}
                </Td>
                <Td>{KIND_LABELS[c.kind]}</Td>
                <Td className="muted">{c.accountLabel ?? c.shop ?? "—"}</Td>
                <Td>
                  <StatusBadge status={c.status} />
                </Td>
                <Td className="muted">{formatDate(c.lastTestedAt)}</Td>
                <Td className="text-right whitespace-nowrap">
                  {c.kind === "shopify" && (
                    <span className="mr-2 text-sm">
                      Export:{" "}
                      <a className="text-accent underline" href={`/api/workspaces/${ws}/connections/${c.id}/export.csv?object=products`}>
                        products
                      </a>{" "}
                      ·{" "}
                      <a className="text-accent underline" href={`/api/workspaces/${ws}/connections/${c.id}/export.csv?object=customers`}>
                        customers
                      </a>
                    </span>
                  )}
                  {manage && c.kind !== "csv" && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={busy === `t${c.id}`}
                        onClick={async () => {
                          setBusy(`t${c.id}`);
                          try {
                            const r = await post<{ test: { ok: boolean; error?: string; account?: string } }>(`/api/workspaces/${ws}/connections/${c.id}/test`);
                            setMsg(r.test.ok ? { tone: "ok", text: `${c.name} works (${r.test.account ?? "connected"}).` } : { tone: "danger", text: `${c.name}: ${r.test.error}` });
                            refresh();
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={busy === `d${c.id}`}
                        onClick={async () => {
                          if (!confirm(`Delete the connection "${c.name}"?`)) return;
                          setBusy(`d${c.id}`);
                          try {
                            await del(`/api/workspaces/${ws}/connections/${c.id}`);
                            refresh();
                          } catch (e) {
                            setMsg({ tone: "danger", text: errorMessage(e) });
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState title="No connections yet" action={manage && <Button onClick={() => setAdding(true)}>Add connection</Button>}>
            Connect Klaviyo, Shopify or Mailchimp. CSV files are added from the Files page.
          </EmptyState>
        )}
      </Card>
      {adding && (
        <AddConnectionModal
          ws={ws}
          onClose={() => setAdding(false)}
          onAdded={(c: Connection, ok: boolean, err?: string) => {
            setAdding(false);
            setMsg(ok ? { tone: "ok", text: `Connected ${c.name}.` } : { tone: "danger", text: `Saved ${c.name}, but the test failed: ${err}` });
            refresh();
          }}
        />
      )}
    </>
  );
}

function AddConnectionModal({ ws, onClose, onAdded }: { ws: string; onClose: () => void; onAdded: (c: Connection, ok: boolean, err?: string) => void }) {
  const catalog = useCatalog();
  const kinds = (catalog.data?.kinds ?? []).filter((k) => k.kind !== "csv");
  const [kind, setKind] = useState<Connection["kind"]>("klaviyo");
  const [name, setName] = useState("");
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [readOnly, setReadOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const info = kinds.find((k) => k.kind === kind);
  const oauth = kind === "shopify" && catalog.data?.shopifyOAuth;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ connection: Connection; test: { ok: boolean; error?: string } }>(`/api/workspaces/${ws}/connections`, { kind, name: name || info?.label, credentials: creds, readOnly });
      onAdded(r.connection, r.test.ok, r.test.error);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a connection"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {oauth ? (
            <Button onClick={() => window.location.assign(`/api/shopify/install/${ws}?shop=${encodeURIComponent(creds.shop ?? "")}&name=${encodeURIComponent(name || "Shopify")}`)} disabled={!creds.shop}>
              Install the Shopify app
            </Button>
          ) : (
            <Button onClick={submit} loading={busy}>
              Save and test
            </Button>
          )}
        </>
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="System" htmlFor="kind">
        <Select
          id="kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as Connection["kind"]);
            setCreds({});
          }}
        >
          {kinds.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label} ({k.roles.join(" & ")})
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Name" htmlFor="cname" hint="For example the brand or store name.">
        <Input id="cname" placeholder={info?.label} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {(oauth ? info?.credentialFields.filter((f) => f.name === "shop") : info?.credentialFields)?.map((f) => (
        <Field key={f.name} label={f.label} htmlFor={f.name} hint={f.help}>
          <Input id={f.name} type={f.secret ? "password" : "text"} autoComplete="off" placeholder={f.placeholder} value={creds[f.name] ?? ""} onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })} />
        </Field>
      ))}
      {!oauth && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} /> Read-only (never write to this system)
        </label>
      )}
      {kind === "shopify" && !oauth && <p className="muted mt-3 text-xs">Paste an Admin API access token from a custom app on the store, with product, customer, order, redirect and inventory scopes.</p>}
    </Modal>
  );
}
